import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

const TIERS = [
  {
    id: "free",
    name: "Free",
    monthly: 0,
    yearly: 0,
    target: "Trying it out, or a light case load",
    note: "Free forever",
    cta: "Get started free",
    features: [
      "10 active cases",
      "15 clients",
      "25 hearings per month",
      "500 MB document storage",
      "1 advocate",
      "Court diary, tasks & notifications",
    ],
  },
  {
    id: "pro",
    name: "Pro Advocate",
    monthly: 499,
    yearly: 4990,
    target: "Independent advocates with a daily board",
    note: "30-day free trial",
    cta: "Start 30-day free trial",
    featured: true,
    features: [
      "Unlimited cases & clients",
      "Unlimited hearings",
      "5 GB document storage",
      "1 advocate",
      "Client portal",
      "Full reports & CSV export",
    ],
  },
  {
    id: "law_firm",
    name: "Law Firm",
    monthly: 999,
    yearly: 9990,
    target: "Chambers working as a team",
    note: "30-day free trial",
    cta: "Start 30-day free trial",
    features: [
      "Everything in Pro Advocate",
      "5 team members",
      "10 GB document storage",
      "Team management & roles",
      "Case assignment",
      "Firm-wide reports",
    ],
  },
];

const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

export const Pricing = () => {
  const [yearly, setYearly] = useState(false);

  return (
    <section
      id="pricing"
      data-testid="landing-pricing"
      className="border-b border-border bg-[#F7F9FC] px-4 py-16 sm:px-6 sm:py-20 lg:px-8"
    >
      <div className="mx-auto max-w-6xl">
        <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">Pricing</p>
        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">
          Plain pricing, in rupees
        </h2>

        <div
          data-testid="pricing-cycle-toggle"
          className="mt-8 inline-flex rounded-md border border-border bg-white p-1"
        >
          <button
            type="button"
            onClick={() => setYearly(false)}
            data-testid="pricing-monthly-btn"
            className={`rounded px-4 py-2 text-sm font-medium transition-colors ${
              !yearly ? "bg-navy text-white" : "text-navy/70 hover:text-navy"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setYearly(true)}
            data-testid="pricing-yearly-btn"
            className={`rounded px-4 py-2 text-sm font-medium transition-colors ${
              yearly ? "bg-navy text-white" : "text-navy/70 hover:text-navy"
            }`}
          >
            Yearly <span className="text-gold-dark">· 2 months off</span>
          </button>
        </div>

        <div className="mt-10 grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
          {TIERS.map((t) => (
            <div
              key={t.id}
              data-testid={`pricing-card-${t.id}`}
              className={`relative rounded-xl bg-white p-7 transition-shadow ${
                t.featured
                  ? "border-2 border-gold shadow-xl lg:-mt-3 lg:pb-9"
                  : "border border-border shadow-sm hover:shadow-md"
              }`}
            >
              {t.featured && (
                <span className="absolute -top-3 left-7 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-navy">
                  Most popular
                </span>
              )}
              <h3 className="font-display text-2xl text-navy">{t.name}</h3>
              <p className="mt-1 min-h-[2.5rem] text-xs text-muted-foreground">{t.target}</p>

              <p className="mt-4 flex items-baseline gap-1.5">
                <span className="font-display text-4xl font-semibold text-navy">
                  {inr(yearly ? t.yearly : t.monthly)}
                </span>
                {t.monthly > 0 && (
                  <span className="text-sm text-muted-foreground">/{yearly ? "year" : "month"}</span>
                )}
              </p>
              <p className="mt-1 text-[11px] font-medium uppercase tracking-wider text-gold-dark">{t.note}</p>

              <Link
                to="/register"
                data-testid={`pricing-cta-${t.id}`}
                className={`mt-6 block rounded-md py-3 text-center text-sm font-semibold transition-all hover:-translate-y-0.5 ${
                  t.featured
                    ? "bg-gold text-navy hover:bg-gold-light"
                    : "border border-navy/20 text-navy hover:bg-secondary"
                }`}
              >
                {t.cta}
              </Link>

              <ul className="mt-7 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm text-navy/80">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="mt-8 text-xs text-muted-foreground">
          Prices exclude applicable taxes. Payments are processed by Razorpay. Cancel any time from Settings —
          your account moves to the Free plan and your data stays.
        </p>
      </div>
    </section>
  );
};
